import React, { useEffect, useRef, useState } from "react";
import { useIsFocused, useRoute } from "@react-navigation/native";
import { RootRouteProps } from "routers/types/stack";
import { _goToAddReminder, _goToRemindersArchive } from "routers/helper";

import {
  View,
  FlatList,
  SafeAreaView,
  TouchableOpacity,
  Platform,
} from "react-native";
import {
  Text,
  Input,
  Error,
  Empty,
  Button,
  Header,
  Loading,
  ReminderBox,
  AddButtonFloat,
  AnimatedInfinityList,
} from "components";
import Filters, { RemindersFiltersT } from "./Filters";
import SelectedFiltersView from "screens/components/SelectedFiltersView";

import { useChmod, useFlag } from "hooks";
import { colors, sizes, strings } from "assets/constants";
import { conditions, liveSearchInput } from "utils";
import { useRecoilValue } from "recoil";
import { authAtom } from "stores";
import css from "styles";

import { quickFiltersData, quickFiltersT } from "./quickFiltersData";

import { useReminders } from "clients/apis";
import { filtersItems } from "assets/data";

const viewOffset = sizes.screenW / 2 - 40;
const categoryItemHeight = 42;
const ListSeparator = () => (
  <View
    style={{
      width: 3,
      height: categoryItemHeight,
      backgroundColor: colors.screensBg,
    }}
  />
);

let timer: ReturnType<typeof setTimeout>;

export default function Reminders() {
  const { chmod } = useChmod();

  const tabs = useRef<FlatList>(null);

  const {
    company: { color },
  } = useRecoilValue(authAtom);
  const isFocused = useIsFocused();
  const { archive } = useRoute<RootRouteProps<"reminders">>().params;

  const { flg: typing, on: startTyping, off: stopTyping } = useFlag();
  const { flg: filters, on: showFilters, off: hideFilters } = useFlag();

  const [search, setSearch] = useState("");
  const [qs, setQs] = useState<RemindersFiltersT>({});

  const [quickFilter, setQuickFilter] = useState<quickFiltersT>(
    quickFiltersData[0]
  );

  const {
    data,
    error,
    isError,
    refetch,
    isLoading,
    isRefetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useReminders({
    title: search,
    date: qs.date,
    type: qs.type?.id,
    customerId: qs.customer?.id,
    archive,
    quickFilters: quickFilter.value,
  });

  const _onApplyFilters = (newFilters: RemindersFiltersT) => {
    hideFilters();
    setQs(newFilters);
  };

  useEffect(() => {
    isFocused && refetch();
  }, [isFocused, refetch]);

  const _renderCategory = (params: {
    item: { id: number; title: string; value: string };
    index: number;
  }) => {
    const { item, index } = params;

    const currentIndex =
      Platform.OS === "web" ? quickFiltersData.length - index - 1 : index;

    const active = quickFilter.id === item.id;
    const _onSelect = () => {
      tabs.current?.scrollToIndex({
        index: currentIndex,
        animated: true,
        viewOffset,
      });
      setQuickFilter(item);
    };
    const contentColor = active ? color : colors.text.gray;

    return (
      <TouchableOpacity
        onPress={_onSelect}
        activeOpacity={sizes.tapOpacity}
        style={{
          ...css.centralElements,
          paddingHorizontal: 20,
          height: categoryItemHeight,
          backgroundColor: active ? colors.white : colors.innerItemsBg,
        }}
      >
        <Text
          size={"sm"}
          weight="bold"
          color={active ? color : colors.textColorGray}
        >
          {item.title}
        </Text>
      </TouchableOpacity>
    );
  };

  const _renderItem = (items: []) => {
    return (
      <View style={{ ...css.wrap, ...css.rRowCenterAlignSpaceBetween }}>
        {items.map((reminder: ReminderT, key: number) => (
          <View key={String(key)} style={css.responsiveBoxContainer}>
            <ReminderBox
              reminder={reminder}
              refreshList={refetch}
              actions={archive ? ["delete"] : ["archives", "delete"]}
            />
          </View>
        ))}
      </View>
    );
  };

  const _renderContent = () => {
    if (isLoading || typing) {
      return <Loading style={{ marginTop: 20 }} />;
    }

    if (isError) {
      return <Error error={error} reset={refetch} loading={isRefetching} />;
    }

    if (data?.pages?.[0]?.data?.data?.length === 0) {
      return <Empty style={{ marginTop: 20 }} message={strings.no_reminder} />;
    }

    return (
      <AnimatedInfinityList
        onRefresh={refetch}
        responseData={data}
        renderItemList={_renderItem}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        paddingTop={sizes.space}
        paddingBottom={100}
      />
    );
  };

  return (
    <>
      <Header title={archive ? strings.reminders_archive : strings.reminders} />
      <>
        <SafeAreaView style={{ ...css.container, ...css.screenBg }}>
          <View
            style={{
              ...css.paddingH,
              ...css.rowCenterAlign,
              marginTop: sizes.space,
            }}
          >
            <Input
              onChangeText={(text) =>
                liveSearchInput(
                  text,
                  search,
                  setSearch,
                  timer,
                  startTyping,
                  stopTyping
                )
              }
              placeholder={strings.search}
              icon={{ name: "search" }}
              hideError
              leftComponent={
                <Button
                  onPress={showFilters}
                  left={4}
                  square={38}
                  radius={sizes.radius.lg}
                  icon={{ name: "sliders", color, size: 24 }}
                  bgColor={colors.white}
                />
              }
            />
          </View>

          <View
            style={{
              height: categoryItemHeight,
              borderBottomWidth: 1,
              borderColor: colors.border + "55",
              backgroundColor: colors.white,
              marginTop: sizes.space,
            }}
          >
            <FlatList
              keyExtractor={(_, key) => String(key)}
              ref={tabs}
              inverted
              horizontal
              showsHorizontalScrollIndicator={false}
              ItemSeparatorComponent={ListSeparator}
              contentContainerStyle={{
                ...css.growContainer,
                ...css.centralElements,
                height: categoryItemHeight,
              }}
              data={quickFiltersData}
              renderItem={_renderCategory}
            />
          </View>

          {conditions.isFiltered(qs) && (
            <View style={{ paddingTop: sizes.space }}>
              <SelectedFiltersView
                filters={qs}
                onChange={setQs}
                filterItems={filtersItems.reminders}
              />
            </View>
          )}

          {_renderContent()}

          <Filters
            visible={filters}
            initials={qs}
            apply={_onApplyFilters}
            hide={hideFilters}
          />
        </SafeAreaView>
      </>
      {!archive && (
        <>
          {chmod("reminder", "POST") && (
            <AddButtonFloat onPress={_goToAddReminder} />
          )}

          <Button
            onPress={_goToRemindersArchive}
            icon={{ name: "archive" }}
            square={50}
            absolute
            bottom={sizes.space + 60}
            left={sizes.space}
          />
        </>
      )}
    </>
  );
}
